import { debounce } from '@/utils';
import { useEffect, useMemo } from 'react';

const events = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'pointermove',
];

/**
 * Tracks user activity on the screen and triggers a callback
 * when no interaction occurs for the specified timeout period.
 * The hook listens to common interaction events such as mouse movement,
 * key presses, scrolling, touch, and pointer movement. Each interaction
 * resets the inactivity timer.
 * When the user remains inactive for the defined time,
 * the provided callback function is executed.
 * @param timeoutSeconds - Time in seconds before the user is considered inactive.
 * @param onIdle - Callback function executed after the inactivity timeout.
 */
export const useScreenInactivity = (
  timeoutSeconds: number,
  onIdle: () => void,
) => {
  const handleIdle = useMemo(
    () => debounce(onIdle, timeoutSeconds * 1000),
    [timeoutSeconds, onIdle],
  );

  useEffect(() => {
    events.forEach((e) =>
      window.addEventListener(e, handleIdle, { passive: true }),
    );

    handleIdle();

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleIdle));
      handleIdle.cancel();
    };
  }, [handleIdle]);
};
